import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Package, TrendingUp, AlertCircle, Plus, Edit2, Trash2, ChevronRight, X,
  CheckCircle, Truck, Archive, BarChart2, Handshake, Clock, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import AppHeader from '@/components/AppHeader';
import AppFooter from '@/components/AppFooter';
import StatusBadge from '@/components/ui/StatusBadge';
import ConfirmModal from '@/components/ui/ConfirmModal';
import OrderModal from '@/components/OrderModal';
import { CATEGORIES, CONDITIONS } from '@/lib/constants';
import { formatINR } from '@/lib/format';
import { getStoredUser, getStoredToken, clearAuth } from '@/lib/authStore';
import { listingsApi, ordersApi, negotiationsApi, addressesApi } from '@/lib/api';
import { Toaster, toast } from 'sonner';

// Lazy-loaded chart components (client-only, code-split out of the main bundle —
// equivalent to the previous next/dynamic({ ssr: false }) usage).
const GmvChart = lazy(() => import('./GmvChart'));
const CategoryChart = lazy(() => import('./CategoryChart'));

function ChartFallback({ height = 200 }) {
  return (
    <div className="flex items-center justify-center" style={{ height }}>
      <div
        className="inline-block w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
        style={{ borderColor: 'var(--primary)' }}
      />
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────
function KpiCard({ label, value, subValue, trend, icon: Icon, variant }) {
  const variantStyles = {
    alert: { border: 'border-t-danger', icon: 'var(--danger)', badge: 'var(--danger-bg)' },
    success: { border: 'border-t-secondary', icon: 'var(--secondary)', badge: 'var(--success-bg)' },
    warning: { border: 'border-t-amber', icon: 'var(--amber)', badge: 'var(--amber-bg)' },
    default: { border: 'border-t-primary', icon: 'var(--primary)' },
  };
  const style = variantStyles[variant ?? 'default'];

  return (
    <div className={`stat-card p-4 ${style.border}`} style={{ borderTopColor: style.icon, borderTopWidth: '3px' }}>
      <div className="flex items-start justify-between mb-3">
        <div
          className="p-2 border"
          style={{
            backgroundColor: style.badge ?? 'rgba(193,64,29,0.08)',
            borderColor: style.icon,
          }}
        >
          <Icon size={16} style={{ color: style.icon }} />
        </div>
        {trend && (
          <span
            className="text-xs font-mono-data px-1.5 py-0.5 border"
            style={{
              fontFamily: 'var(--font-mono)',
              color: trend.startsWith('+') ? 'var(--secondary)' : 'var(--danger)',
              borderColor: trend.startsWith('+') ? 'var(--secondary)' : 'var(--danger)',
              backgroundColor: trend.startsWith('+') ? 'var(--success-bg)' : 'var(--danger-bg)',
            }}
          >
            {trend} WoW
          </span>
        )}
      </div>
      <p
        className="text-2xl font-heading font-mono-data mb-1"
        style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--foreground)' }}
      >
        {value}
      </p>
      <p className="text-xs font-heading tracking-stamp" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
        {label}
      </p>
      {subValue && (
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{subValue}</p>
      )}
    </div>
  );
}

// ─── Create/Edit Listing Modal ────────────────────────────
const MAX_LISTING_IMAGES = 6;

function ListingFormModal({ editListing, onClose, onSave }) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: editListing
      ? {
          title: editListing.title,
          category: editListing.category,
          quantity: editListing.quantity,
          price: editListing.price,
          marketValue: editListing.marketValue ?? '',
          description: editListing.description,
          city: editListing.city,
          imageUrl: editListing.imageUrl ?? '',
          brand: editListing.brand ?? '',
          condition: editListing.condition ?? 'New',
          moq: editListing.moq ?? 1,
          warranty: editListing.warranty ?? '',
          hsnCode: editListing.hsnCode ?? '',
          negotiable: editListing.negotiable ?? false,
          autoAcceptMaxDiscountPct: editListing.autoAcceptMaxDiscountPct ?? '',
          sellerNotes: editListing.sellerNotes ?? '',
        }
      : { condition: 'New', moq: 1, negotiable: false },
  });

  const watchedPrice = Number(watch('price')) || 0;
  const watchedNegotiable = watch('negotiable');
  const watchedMarketValue = Number(watch('marketValue')) || 0;
  const discountPreview =
    watchedMarketValue > 0 && watchedMarketValue > watchedPrice
      ? Math.round(((watchedMarketValue - watchedPrice) / watchedMarketValue) * 100)
      : null;

  // Product photos — multiple images, first one is the primary/cover image.
  // Existing images (from editing a listing) start as { previewUrl, file: null };
  // newly-added ones carry the actual File so we can (in a real backend) upload it.
  const initialImages = editListing
    ? [editListing.imageUrl, ...(editListing.galleryUrls || [])].filter(Boolean).map((url) => ({ previewUrl: url, file: null }))
    : [];
  const [images, setImages] = useState(initialImages);
  const [imageError, setImageError] = useState('');
  const fileInputRef = React.useRef(null);

  // Specifications — seller-entered key/value spec sheet for the product
  // detail page. Kept as local state (not RHF) since it's a dynamic list.
  const initialSpecs = editListing?.specifications
    ? Object.entries(editListing.specifications).map(([key, value]) => ({ key, value: String(value) }))
    : [{ key: '', value: '' }];
  const [specs, setSpecs] = useState(initialSpecs.length ? initialSpecs : [{ key: '', value: '' }]);

  const updateSpec = (index, field, value) => {
    setSpecs((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };
  const addSpecRow = () => setSpecs((prev) => [...prev, { key: '', value: '' }]);
  const removeSpecRow = (index) => setSpecs((prev) => prev.filter((_, i) => i !== index));

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setImageError('');

    const room = MAX_LISTING_IMAGES - images.length;
    if (room <= 0) {
      setImageError(`You can upload up to ${MAX_LISTING_IMAGES} photos.`);
      return;
    }
    const toAdd = files.slice(0, room);
    if (files.length > room) setImageError(`Only added ${room} of ${files.length} — ${MAX_LISTING_IMAGES} photo limit reached.`);

    toAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImages((prev) => [...prev, { previewUrl: ev.target?.result, file }]);
      };
      reader.readAsDataURL(file);
    });

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImageAt = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImageError('');
  };

  const onSubmit = async (data) => {
    if (images.length === 0) {
      setImageError('At least one product photo is required.');
      return;
    }

    // BACKEND INTEGRATION POINT: upload each new image.file to storage, get
    // its URL, then POST/PUT listing. This mock keeps everything as base64
    // data URIs, same as the rest of this environment's upload flow.
    await new Promise((r) => setTimeout(r, 800));

    const specifications = Object.fromEntries(
      specs.filter((s) => s.key.trim() && s.value.trim()).map((s) => [s.key.trim(), s.value.trim()])
    );

    const [primaryImage, ...galleryImages] = images.map((img) => img.previewUrl);

    onSave({
      ...data,
      marketValue: data.marketValue ? Number(data.marketValue) : undefined,
      autoAcceptMaxDiscountPct: data.negotiable && data.autoAcceptMaxDiscountPct ? Number(data.autoAcceptMaxDiscountPct) : 0,
      imageUrl: primaryImage,
      galleryUrls: galleryImages,
      specifications,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div
        className="w-full max-w-lg mx-4 paper-texture border-2 border-foreground ink-shadow max-h-[90vh] overflow-y-auto"
        style={{ animation: 'fadeIn 150ms ease' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-foreground sticky top-0 z-10" style={{ backgroundColor: 'var(--card)' }}>
          <div className="flex items-center gap-2">
            <Package size={16} style={{ color: 'var(--primary)' }} />
            <h3 className="font-heading text-base tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
              {editListing ? 'EDIT LISTING' : 'NEW LISTING'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:opacity-70 transition-opacity">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 flex flex-col gap-4">
          {editListing?.adminStatus === 'rejected' && (
            <div className="p-3 border" style={{ borderColor: 'var(--danger)', backgroundColor: 'var(--danger-bg)' }}>
              <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--danger)' }}>
                REJECTED BY ADMIN — HERE'S WHY
              </p>
              <p className="text-sm" style={{ color: 'var(--foreground)' }}>{editListing.adminNotes || 'No reason provided.'}</p>
              <p className="text-xs mt-2" style={{ color: 'var(--muted-foreground)' }}>
                Make the changes needed and save — this will automatically resubmit the listing for admin review.
              </p>
            </div>
          )}
          {/* Product Photos — multiple images */}
          <div>
            <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              PRODUCT PHOTOS <span style={{ color: 'var(--primary)' }}>*</span>
            </label>
            <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
              Upload up to {MAX_LISTING_IMAGES} photos — JPG, PNG, WEBP, up to 5MB each. The first photo is the cover
              image shown on the marketplace; the rest appear in the product gallery.
            </p>

            <div className="grid grid-cols-3 gap-2">
              {images.map((img, i) => (
                <div key={`img-${i}`} className="relative border-2" style={{ borderColor: i === 0 ? 'var(--primary)' : 'var(--border)' }}>
                  <img src={img.previewUrl} alt={`Product ${i + 1}`} className="w-full object-cover" style={{ height: '90px' }} />
                  {i === 0 && (
                    <span
                      className="absolute bottom-0 left-0 px-1.5 py-0.5 text-xs font-heading tracking-stamp"
                      style={{ backgroundColor: 'var(--primary)', color: 'white', fontFamily: 'var(--font-heading)', fontSize: '10px' }}
                    >
                      COVER
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImageAt(i)}
                    className="absolute top-1 right-1 p-0.5 border-2 border-foreground"
                    style={{ backgroundColor: 'var(--card)' }}
                    title="Remove photo"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}

              {images.length < MAX_LISTING_IMAGES && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed flex flex-col items-center justify-center gap-1 hover:opacity-80 transition-opacity"
                  style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)', height: '90px' }}
                >
                  <Package size={16} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="text-xs font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>
                    ADD PHOTO
                  </span>
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={handleImagesChange}
            />
            {imageError && <p className="text-xs mt-1.5" style={{ color: 'var(--danger)' }}>{imageError}</p>}
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              LISTING TITLE <span style={{ color: 'var(--primary)' }}>*</span>
            </label>
            <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
              Be specific — include brand, model, condition
            </p>
            <input
              type="text"
              className="input-ledger w-full px-3 py-2.5 text-sm"
              placeholder="e.g. Honda CB300R Brake Pads — Overstock 2024 Batch"
              {...register('title', { required: 'Title is required', minLength: { value: 10, message: 'Title must be at least 10 characters' } })}
            />
            {errors.title && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.title.message}</p>}
          </div>

          {/* Category + City */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                CATEGORY <span style={{ color: 'var(--primary)' }}>*</span>
              </label>
              <div className="relative">
                <select
                  className="input-ledger w-full px-3 py-2.5 text-sm appearance-none"
                  {...register('category', { required: 'Category is required' })}
                >
                  <option value="">Select...</option>
                  {CATEGORIES.map((cat) => (
                    <option key={`form-cat-${cat}`} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              {errors.category && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.category.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                LOCATION <span style={{ color: 'var(--primary)' }}>*</span>
              </label>
              <input
                type="text"
                className="input-ledger w-full px-3 py-2.5 text-sm"
                placeholder="Bengaluru"
                {...register('city', { required: 'City is required' })}
              />
              {errors.city && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.city.message}</p>}
            </div>
          </div>

          {/* Quantity + Price */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                QUANTITY (UNITS) <span style={{ color: 'var(--primary)' }}>*</span>
              </label>
              <input
                type="number"
                min={1}
                className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data"
                style={{ fontFamily: 'var(--font-mono)' }}
                placeholder="500"
                {...register('quantity', {
                  required: 'Quantity is required',
                  min: { value: 1, message: 'Must be at least 1' },
                  valueAsNumber: true,
                })}
              />
              {errors.quantity && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.quantity.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                PRICE PER UNIT (₹) <span style={{ color: 'var(--primary)' }}>*</span>
              </label>
              <input
                type="number"
                min={0.01}
                step="0.01"
                className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data"
                style={{ fontFamily: 'var(--font-mono)' }}
                placeholder="250.00"
                {...register('price', {
                  required: 'Price is required',
                  min: { value: 0.01, message: 'Price must be positive' },
                  valueAsNumber: true,
                })}
              />
              {errors.price && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.price.message}</p>}
            </div>
          </div>

          {/* Market Value + discount preview */}
          <div>
            <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              MARKET VALUE / MRP (₹) <span style={{ color: 'var(--muted-foreground)' }}>(OPTIONAL)</span>
            </label>
            <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
              The original/retail price — shown as a "% off market value" badge on your listing
            </p>
            <input
              type="number"
              min={0.01}
              step="0.01"
              className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data"
              style={{ fontFamily: 'var(--font-mono)' }}
              placeholder="e.g. 500.00"
              {...register('marketValue', {
                validate: (v) => !v || Number(v) >= watchedPrice || 'Market value cannot be less than your selling price',
              })}
            />
            {errors.marketValue && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.marketValue.message}</p>}
            {discountPreview !== null && (
              <p className="text-xs mt-1.5 font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)', color: 'var(--secondary)' }}>
                → Will show as {discountPreview}% OFF market value
              </p>
            )}
          </div>

          {/* Bulk price negotiation settings */}
          <div className="p-3 border" style={{ borderColor: 'var(--secondary)', backgroundColor: 'var(--success-bg)' }}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" {...register('negotiable')} />
              <span className="text-xs font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
                ALLOW BULK BUYERS TO NEGOTIATE PRICE (MAX 10% DISCOUNT)
              </span>
            </label>
            <p className="text-xs mt-1.5 ml-6" style={{ color: 'var(--muted-foreground)' }}>
              Only shown to buyers whose quantity qualifies as bulk for this listing. Every offer is capped at 10%
              off automatically — buyers can never ask for more.
            </p>

            {watchedNegotiable && (
              <div className="mt-3 ml-6">
                <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  AUTO-ACCEPT OFFERS UP TO <span style={{ color: 'var(--muted-foreground)' }}>(OPTIONAL, 0–10%)</span>
                </label>
                <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  Offers at or below this discount are approved instantly, no action needed from you. Leave at 0 to
                  review every offer yourself.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={10}
                    step="0.5"
                    className="input-ledger w-24 px-3 py-2 text-sm font-mono-data"
                    style={{ fontFamily: 'var(--font-mono)' }}
                    placeholder="0"
                    {...register('autoAcceptMaxDiscountPct', {
                      validate: (v) => v === '' || (Number(v) >= 0 && Number(v) <= 10) || 'Must be between 0 and 10',
                    })}
                  />
                  <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>%</span>
                </div>
                {errors.autoAcceptMaxDiscountPct && (
                  <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.autoAcceptMaxDiscountPct.message}</p>
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              DESCRIPTION <span style={{ color: 'var(--primary)' }}>*</span>
            </label>
            <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
              Include condition, reason for clearance, packaging details
            </p>
            <textarea
              rows={3}
              className="input-ledger w-full px-3 py-2.5 text-sm resize-none"
              placeholder="Original OEM parts, 2024 batch, overordered by distributor. Sealed boxes, full warranty..."
              {...register('description', {
                required: 'Description is required',
                minLength: { value: 20, message: 'Description must be at least 20 characters' },
              })}
            />
            {errors.description && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.description.message}</p>}
          </div>

          {/* Product Detail Page fields ------------------------------------- */}
          <div className="pt-2 border-t border-border">
            <p className="text-xs font-heading tracking-stamp mb-3" style={{ fontFamily: 'var(--font-heading)', color: 'var(--primary)' }}>
              FULL PRODUCT DETAILS — SHOWN ON THE PRODUCT PAGE
            </p>
          </div>

          {/* Brand + Condition */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                BRAND <span style={{ color: 'var(--muted-foreground)' }}>(OPTIONAL)</span>
              </label>
              <input
                type="text"
                className="input-ledger w-full px-3 py-2.5 text-sm"
                placeholder="e.g. Honda OEM"
                {...register('brand')}
              />
            </div>
            <div>
              <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                CONDITION <span style={{ color: 'var(--primary)' }}>*</span>
              </label>
              <select className="input-ledger w-full px-3 py-2.5 text-sm appearance-none" {...register('condition', { required: true })}>
                {CONDITIONS.map((c) => (
                  <option key={`cond-${c}`} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* MOQ + Warranty */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                MIN. ORDER QTY (MOQ) <span style={{ color: 'var(--primary)' }}>*</span>
              </label>
              <input
                type="number"
                min={1}
                className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data"
                style={{ fontFamily: 'var(--font-mono)' }}
                placeholder="1"
                {...register('moq', { required: true, min: { value: 1, message: 'Must be at least 1' }, valueAsNumber: true })}
              />
              {errors.moq && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.moq.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                WARRANTY <span style={{ color: 'var(--muted-foreground)' }}>(OPTIONAL)</span>
              </label>
              <input
                type="text"
                className="input-ledger w-full px-3 py-2.5 text-sm"
                placeholder="e.g. 6 months"
                {...register('warranty')}
              />
            </div>
          </div>

          {/* HSN Code */}
          <div>
            <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              HSN CODE <span style={{ color: 'var(--muted-foreground)' }}>(OPTIONAL — FOR GST-READY BUYERS)</span>
            </label>
            <input
              type="text"
              className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data"
              style={{ fontFamily: 'var(--font-mono)' }}
              placeholder="e.g. 8714"
              {...register('hsnCode')}
            />
          </div>

          {/* Specifications — dynamic key/value rows */}
          <div>
            <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              SPECIFICATIONS <span style={{ color: 'var(--muted-foreground)' }}>(OPTIONAL)</span>
            </label>
            <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
              Shown as a spec sheet on the product page — e.g. Material, Size, Pack Size
            </p>
            <div className="flex flex-col gap-2">
              {specs.map((spec, i) => (
                <div key={`spec-row-${i}`} className="flex gap-2">
                  <input
                    type="text"
                    value={spec.key}
                    onChange={(e) => updateSpec(i, 'key', e.target.value)}
                    className="input-ledger flex-1 px-3 py-2 text-xs"
                    placeholder="Key (e.g. Material)"
                  />
                  <input
                    type="text"
                    value={spec.value}
                    onChange={(e) => updateSpec(i, 'value', e.target.value)}
                    className="input-ledger flex-1 px-3 py-2 text-xs"
                    placeholder="Value (e.g. Cotton)"
                  />
                  <button
                    type="button"
                    onClick={() => removeSpecRow(i)}
                    className="p-2 border hover:opacity-70"
                    style={{ borderColor: 'var(--danger)' }}
                  >
                    <X size={13} style={{ color: 'var(--danger)' }} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addSpecRow}
              className="mt-2 btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5"
            >
              <Plus size={12} />
              ADD SPECIFICATION
            </button>
          </div>

          {/* Note to Admin — private, never shown to buyers */}
          <div>
            <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              NOTE TO ADMIN <span style={{ color: 'var(--muted-foreground)' }}>(OPTIONAL — VISIBLE ONLY TO THE ADMIN REVIEWER)</span>
            </label>
            <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
              Any context that helps review this listing faster — e.g. "this is a warranty-return batch, still sealed."
            </p>
            <textarea
              rows={2}
              className="input-ledger w-full px-3 py-2.5 text-sm resize-none"
              placeholder="Optional note for the admin reviewing this listing..."
              {...register('sellerNotes')}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-border sticky bottom-0" style={{ backgroundColor: 'var(--card)', paddingTop: '12px' }}>
            <button type="button" onClick={onClose} className="btn-secondary flex-1 px-4 py-2.5 text-sm">
              CANCEL
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex-1 px-4 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  SAVING...
                </>
              ) : (
                editListing ? 'SAVE CHANGES' : 'PUBLISH LISTING'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Seller Listings Tab ──────────────────────────────────
function SellerListingsTab({ listings, onEdit, onDelete, onNew, onToggleStatus }) {
  return (
    <div>
      {/* Tab Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            <span className="font-mono-data font-medium" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-mono)' }}>
              {listings.length}
            </span>{' '}
            listing{listings.length !== 1 ? 's' : ''} in your inventory
          </p>
        </div>
        <button
          onClick={onNew}
          className="btn-primary px-4 py-2 text-xs flex items-center gap-1.5"
        >
          <Plus size={13} />
          NEW LISTING
        </button>
      </div>

      {listings.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-16 border-2 border-dashed"
          style={{ borderColor: 'var(--border)' }}
        >
          <Package size={36} className="mb-3" style={{ color: 'var(--muted-foreground)' }} />
          <h3 className="font-heading text-base tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
            NO LISTINGS YET
          </h3>
          <p className="text-sm text-center max-w-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
            Post your first deadstock listing to start connecting with buyers across India.
          </p>
          <button onClick={onNew} className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
            <Plus size={14} />
            CREATE FIRST LISTING
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--foreground)' }}>
                {['LISTING TITLE', 'CATEGORY', 'QTY AVAIL.', 'UNIT PRICE', 'STATUS', 'ADMIN REVIEW', 'ACTIONS'].map((col) => (
                  <th
                    key={`th-${col}`}
                    className="px-3 py-2.5 text-left text-xs font-heading tracking-stamp whitespace-nowrap"
                    style={{ color: 'var(--background)', fontFamily: 'var(--font-heading)' }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listings.map((listing, idx) => (
                <tr
                  key={listing.id}
                  className="border-b transition-colors hover:opacity-90"
                  style={{
                    borderColor: 'var(--border)',
                    backgroundColor: idx % 2 === 0 ? 'var(--card)' : 'var(--background)',
                  }}
                >
                  <td className="px-3 py-3 max-w-[240px]">
                    <p className="text-sm font-medium truncate" title={listing.title}>
                      {listing.title}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                      {listing.city}
                    </p>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span
                      className="text-xs font-heading tracking-stamp px-2 py-0.5 border"
                      style={{
                        borderColor: 'var(--border)',
                        color: 'var(--muted-foreground)',
                        fontFamily: 'var(--font-heading)',
                      }}
                    >
                      {listing.category}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span
                      className="font-mono-data text-sm"
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontVariantNumeric: 'tabular-nums',
                        color: listing.quantity === 0 ? 'var(--danger)' : 'var(--foreground)',
                      }}
                    >
                      {listing.quantity.toLocaleString('en-IN')}
                    </span>
                    {listing.quantity === 0 && (
                      <span className="ml-1 text-xs" style={{ color: 'var(--danger)' }}>OUT</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span
                      className="font-mono-data text-sm font-medium"
                      style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--primary)' }}
                    >
                      {formatINR(listing.price)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge status={listing.status} size="sm" />
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className="text-xs font-heading tracking-stamp px-2 py-0.5 border inline-block"
                      style={{
                        fontFamily: 'var(--font-heading)',
                        borderColor: listing.adminStatus === 'approved' ? 'var(--secondary)' : listing.adminStatus === 'rejected' ? 'var(--danger)' : 'var(--amber)',
                        color: listing.adminStatus === 'approved' ? 'var(--secondary)' : listing.adminStatus === 'rejected' ? 'var(--danger)' : 'var(--amber)',
                        backgroundColor: listing.adminStatus === 'approved' ? 'var(--success-bg)' : listing.adminStatus === 'rejected' ? 'var(--danger-bg)' : 'var(--amber-bg)',
                      }}
                      title={listing.adminNotes || undefined}
                    >
                      {listing.adminStatus === 'approved' && 'APPROVED'}
                      {listing.adminStatus === 'pending' && 'AWAITING REVIEW'}
                      {listing.adminStatus === 'rejected' && 'REJECTED'}
                    </span>
                    {listing.adminStatus === 'rejected' && listing.adminNotes && (
                      <p className="text-xs mt-1 max-w-[160px]" style={{ color: 'var(--muted-foreground)' }}>{listing.adminNotes}</p>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      {listing.quantity > 0 && (
                        <button
                          onClick={() => onToggleStatus(listing)}
                          className="p-1.5 border transition-all hover:opacity-80"
                          style={{ borderColor: listing.status === 'active' ? 'var(--amber)' : 'var(--secondary)' }}
                          title={listing.status === 'active' ? 'Pause this listing (hide from marketplace)' : 'Reactivate this listing'}
                        >
                          {listing.status === 'active' ? (
                            <Clock size={13} style={{ color: 'var(--amber)' }} />
                          ) : (
                            <CheckCircle size={13} style={{ color: 'var(--secondary)' }} />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(listing)}
                        className="p-1.5 border transition-all hover:opacity-80"
                        style={{ borderColor: 'var(--border)' }}
                        title="Edit this listing"
                      >
                        <Edit2 size={13} style={{ color: 'var(--foreground)' }} />
                      </button>
                      <button
                        onClick={() => onDelete(listing)}
                        className="p-1.5 border transition-all hover:opacity-80"
                        style={{ borderColor: 'var(--danger)', backgroundColor: 'var(--danger-bg)' }}
                        title="Delete this listing — this cannot be undone"
                      >
                        <Trash2 size={13} style={{ color: 'var(--danger)' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Order Status Action Button ───────────────────────────
function OrderActionButton({ order, onAdvance }) {
  const transitions = {
    pending_confirmation: { label: 'Pending Confirmation', next: 'confirmed', nextLabel: 'Confirm Order' },
    confirmed: { label: 'Confirmed', next: 'in_transit', nextLabel: 'Mark In Transit' },
    in_transit: { label: 'In Transit', next: 'delivered', nextLabel: 'Mark Delivered' },
  };

  const [pendingStatus, setPendingStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courierName, setCourierName] = useState('');
  const [shipError, setShipError] = useState('');

  if (order.paymentStatus !== 'paid') {
    return (
      <span className="text-xs font-heading tracking-stamp" style={{ color: 'var(--amber)', fontFamily: 'var(--font-heading)' }}>
        AWAITING PAYMENT
      </span>
    );
  }

  const t = transitions[order.status];
  if (!t) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-heading tracking-stamp" style={{ color: 'var(--secondary)', fontFamily: 'var(--font-heading)' }}>
          COMPLETE ✓
        </span>
        {order.trackingNumber && (
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Truck size={10} className="inline mr-1" style={{ verticalAlign: '-1px' }} />
            {order.courierName} · <span className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{order.trackingNumber}</span>
          </p>
        )}
      </div>
    );
  }

  const needsTracking = pendingStatus === 'in_transit';

  const handleConfirm = async () => {
    setShipError('');
    if (needsTracking) {
      if (!trackingNumber.trim() || !courierName.trim()) {
        setShipError('Tracking number and courier name are both required to mark this shipped.');
        return;
      }
    }
    setSubmitting(true);
    try {
      await onAdvance(
        order.id,
        pendingStatus,
        needsTracking ? { trackingNumber: trackingNumber.trim(), courierName: courierName.trim() } : undefined
      );
      setTrackingNumber('');
      setCourierName('');
    } finally {
      setSubmitting(false);
      setPendingStatus('');
    }
  };

  return (
    <div className="flex flex-col gap-1.5" style={{ minWidth: '160px' }}>
      <div className="flex items-center gap-1.5">
        <select
          value={pendingStatus || order.status}
          onChange={(e) => { setPendingStatus(e.target.value === order.status ? '' : e.target.value); setShipError(''); }}
          className="input-ledger px-2 py-1.5 text-xs"
          style={{ minWidth: '150px' }}
        >
          <option value={order.status}>{t.label} (current)</option>
          <option value={t.next}>→ {t.nextLabel}</option>
        </select>
        {pendingStatus && pendingStatus !== order.status && !needsTracking && (
          <div className="flex items-center gap-1">
            <button
              onClick={handleConfirm}
              disabled={submitting}
              title="Confirm this status change"
              className="btn-success px-2 py-1.5 disabled:opacity-60"
            >
              <CheckCircle size={12} />
            </button>
            <button
              onClick={() => setPendingStatus('')}
              title="Undo — keep current status"
              className="p-1.5 border"
              style={{ borderColor: 'var(--border)' }}
            >
              <X size={12} />
            </button>
          </div>
        )}
      </div>

      {needsTracking && (
        <div className="flex flex-col gap-1 p-2 border" style={{ borderColor: 'var(--primary)', backgroundColor: 'rgba(193,64,29,0.05)' }}>
          <p className="text-xs font-heading tracking-stamp flex items-center gap-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--primary)' }}>
            <Truck size={11} /> SHIPMENT DETAILS <span style={{ color: 'var(--danger)' }}>*</span>
          </p>
          <input
            value={courierName}
            onChange={(e) => setCourierName(e.target.value)}
            placeholder="Courier name (e.g. Delhivery)"
            className="input-ledger px-2 py-1.5 text-xs"
          />
          <input
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="Tracking / AWB number"
            className="input-ledger px-2 py-1.5 text-xs font-mono-data"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
          {shipError && <p className="text-xs" style={{ color: 'var(--danger)' }}>{shipError}</p>}
          <div className="flex items-center gap-1 mt-0.5">
            <button
              onClick={handleConfirm}
              disabled={submitting}
              className="btn-success flex-1 px-2 py-1.5 text-xs flex items-center justify-center gap-1 disabled:opacity-60"
            >
              <CheckCircle size={12} /> {submitting ? 'SAVING...' : 'CONFIRM SHIPPED'}
            </button>
            <button
              onClick={() => { setPendingStatus(''); setShipError(''); }}
              title="Undo — keep current status"
              className="p-1.5 border"
              style={{ borderColor: 'var(--border)' }}
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      {order.status === 'in_transit' && order.trackingNumber && (
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          <Truck size={10} className="inline mr-1" style={{ verticalAlign: '-1px' }} />
          {order.courierName} · <span className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{order.trackingNumber}</span>
        </p>
      )}
    </div>
  );
}

// ─── Seller Orders Tab ────────────────────────────────────
function SellerOrdersTab({ orders, onAdvance }) {
  const pendingCount = orders.filter((o) => o.status === 'pending_confirmation').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            <span className="font-mono-data font-medium" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-mono)' }}>
              {orders.length}
            </span>{' '}
            total orders received
          </p>
          {pendingCount > 0 && (
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 border text-xs"
              style={{
                backgroundColor: 'var(--danger-bg)',
                borderColor: 'var(--danger)',
                color: 'var(--danger)',
              }}
            >
              <AlertCircle size={12} />
              <span className="font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
                {pendingCount} PENDING CONFIRMATION
              </span>
            </div>
          )}
        </div>
      </div>

      {orders.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-16 border-2 border-dashed"
          style={{ borderColor: 'var(--border)' }}
        >
          <TrendingUp size={36} className="mb-3" style={{ color: 'var(--muted-foreground)' }} />
          <h3 className="font-heading text-base tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
            NO ORDERS YET
          </h3>
          <p className="text-sm text-center max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
            Orders from buyers will appear here once your listings go live. Make sure your listings are active.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--foreground)' }}>
                {['ORDER ID', 'LISTING', 'BUYER', 'SHIP TO', 'QTY', 'ORDER VALUE', 'PAYMENT', 'STATUS', 'ACTION'].map((col) => (
                  <th
                    key={`seller-order-th-${col}`}
                    className="px-3 py-2.5 text-left text-xs font-heading tracking-stamp whitespace-nowrap"
                    style={{ color: 'var(--background)', fontFamily: 'var(--font-heading)' }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((order, idx) => (
                <tr
                  key={order.id}
                  className="border-b transition-colors"
                  style={{
                    borderColor: 'var(--border)',
                    backgroundColor:
                      order.status === 'pending_confirmation' ? 'var(--amber-bg)'
                        : idx % 2 === 0
                        ? 'var(--card)'
                        : 'var(--background)',
                  }}
                >
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="font-mono-data text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted-foreground)' }}>
                      #{order.id.replace('order-', '')}
                    </span>
                  </td>
                  <td className="px-3 py-3 max-w-[200px]">
                    <p className="text-xs font-medium truncate" title={order.listingTitle}>
                      {order.listingTitle}
                    </p>
                  </td>
                  <td className="px-3 py-3 max-w-[160px]">
                    <p className="text-xs font-medium truncate">{order.buyerName}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{order.buyerCompany}</p>
                  </td>
                  <td className="px-3 py-3 max-w-[180px]">
                    {order.shipping?.name ? (
                      <div
                        className="text-xs cursor-help"
                        title={`${order.shipping.name} · ${order.shipping.mobile}${order.shipping.altMobile ? ' / ' + order.shipping.altMobile : ''}\n${order.shipping.addressLine1}${order.shipping.addressLine2 ? ', ' + order.shipping.addressLine2 : ''}, ${order.shipping.city}, ${order.shipping.state} ${order.shipping.pincode}`}
                      >
                        <p className="truncate">{order.shipping.city}, {order.shipping.state}</p>
                        <p style={{ color: 'var(--muted-foreground)' }}>{order.shipping.pincode} · {order.shipping.mobile}</p>
                      </div>
                    ) : (
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="font-mono-data text-sm" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                      {order.quantity.toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span
                      className="font-mono-data text-sm font-medium"
                      style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--primary)' }}
                    >
                      {formatINR(order.totalValue)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className="text-xs font-heading tracking-stamp px-2 py-0.5 border inline-block"
                      style={{
                        fontFamily: 'var(--font-heading)',
                        borderColor: order.paymentStatus === 'paid' ? 'var(--secondary)' : order.paymentStatus === 'failed' ? 'var(--danger)' : 'var(--amber)',
                        color: order.paymentStatus === 'paid' ? 'var(--secondary)' : order.paymentStatus === 'failed' ? 'var(--danger)' : 'var(--amber)',
                        backgroundColor: order.paymentStatus === 'paid' ? 'var(--success-bg)' : order.paymentStatus === 'failed' ? 'var(--danger-bg)' : 'var(--amber-bg)',
                      }}
                    >
                      {order.paymentStatus?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge status={order.status} size="sm" />
                  </td>
                  <td className="px-3 py-3">
                    <OrderActionButton order={order} onAdvance={onAdvance} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Buyer Orders Tab ─────────────────────────────────────
function BuyerOrdersTab({ orders }) {
  const statusSteps = ['pending_confirmation', 'confirmed', 'in_transit', 'delivered'];

  return (
    <div>
      <div className="mb-4">
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          <span className="font-mono-data font-medium" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-mono)' }}>
            {orders.length}
          </span>{' '}
          order{orders.length !== 1 ? 's' : ''} placed
        </p>
      </div>

      {orders.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-16 border-2 border-dashed"
          style={{ borderColor: 'var(--border)' }}
        >
          <Package size={36} className="mb-3" style={{ color: 'var(--muted-foreground)' }} />
          <h3 className="font-heading text-base tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
            NO ORDERS PLACED
          </h3>
          <p className="text-sm text-center max-w-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
            You haven't placed any orders yet. Browse the marketplace to find deadstock deals.
          </p>
          <a href="/marketplace" className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
            <Package size={14} />
            BROWSE MARKETPLACE
          </a>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {orders.map((order) => {
            const currentStepIdx = statusSteps.indexOf(order.status);
            return (
              <div
                key={order.id}
                className="border-2 border-foreground"
                style={{ backgroundColor: 'var(--card)', boxShadow: '2px 2px 0 var(--muted)' }}
              >
                {/* Order Header */}
                <div
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3 border-b-2 border-foreground"
                  style={{ backgroundColor: 'var(--background)' }}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono-data text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted-foreground)' }}>
                      ORDER #{order.id.replace('buyer-order-', '').replace('order-', '')}
                    </span>
                    <ChevronRight size={12} style={{ color: 'var(--muted-foreground)' }} />
                    <p className="text-sm font-medium truncate max-w-[260px]" title={order.listingTitle}>
                      {order.listingTitle}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-heading tracking-stamp px-2 py-0.5 border inline-block"
                      style={{
                        fontFamily: 'var(--font-heading)',
                        borderColor: order.paymentStatus === 'paid' ? 'var(--secondary)' : order.paymentStatus === 'failed' ? 'var(--danger)' : 'var(--amber)',
                        color: order.paymentStatus === 'paid' ? 'var(--secondary)' : order.paymentStatus === 'failed' ? 'var(--danger)' : 'var(--amber)',
                        backgroundColor: order.paymentStatus === 'paid' ? 'var(--success-bg)' : order.paymentStatus === 'failed' ? 'var(--danger-bg)' : 'var(--amber-bg)',
                      }}
                    >
                      {order.paymentStatus === 'paid' ? 'PAID' : order.paymentStatus?.toUpperCase()}
                    </span>
                    <StatusBadge status={order.status} size="sm" />
                  </div>
                </div>

                <div className="p-4">
                  {/* Order Details */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                    <div>
                      <p className="text-xs font-heading tracking-stamp mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>SELLER</p>
                      <p className="text-xs font-medium">{order.sellerCompany}</p>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{order.sellerCity}</p>
                    </div>
                    <div>
                      <p className="text-xs font-heading tracking-stamp mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>QUANTITY</p>
                      <p className="text-sm font-mono-data font-medium" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                        {order.quantity.toLocaleString('en-IN')} units
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-heading tracking-stamp mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>UNIT PRICE</p>
                      <p className="text-sm font-mono-data font-medium" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--primary)' }}>
                        {formatINR(order.price)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-heading tracking-stamp mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>SUBTOTAL</p>
                      <p className="text-sm font-mono-data font-medium" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--primary)' }}>
                        {formatINR(order.totalValue)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-heading tracking-stamp mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>TOTAL PAID</p>
                      <p className="text-sm font-mono-data font-medium" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--secondary)' }} title={`Includes ${order.commissionRate}% commission + ${order.gstRate}% GST on commission`}>
                        {formatINR(order.totalPayable)}
                      </p>
                    </div>
                  </div>

                  {order.shipping?.name && (
                    <div className="mb-4 p-2.5 border flex items-start gap-2" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}>
                      <Package size={13} style={{ color: 'var(--muted-foreground)', marginTop: '1px', flexShrink: 0 }} />
                      <div>
                        <p className="text-xs font-heading tracking-stamp mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
                          SHIP TO
                        </p>
                        <p className="text-xs">
                          {order.shipping.name} · {order.shipping.mobile}
                          {order.shipping.altMobile ? ` / ${order.shipping.altMobile}` : ''}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          {order.shipping.addressLine1}
                          {order.shipping.addressLine2 ? `, ${order.shipping.addressLine2}` : ''}, {order.shipping.city}, {order.shipping.state} {order.shipping.pincode}
                        </p>
                      </div>
                    </div>
                  )}

                  {order.trackingNumber && (
                    <div className="mb-4 p-2.5 border flex items-start gap-2" style={{ borderColor: 'var(--secondary)', backgroundColor: 'var(--success-bg)' }}>
                      <Truck size={13} style={{ color: 'var(--secondary)', marginTop: '1px', flexShrink: 0 }} />
                      <div>
                        <p className="text-xs font-heading tracking-stamp mb-0.5" style={{ color: 'var(--secondary)', fontFamily: 'var(--font-heading)' }}>
                          SHIPMENT TRACKING
                        </p>
                        <p className="text-xs">
                          {order.courierName} · <span className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{order.trackingNumber}</span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Progress Pipeline */}
                  <div>
                    <p className="text-xs font-heading tracking-stamp mb-2" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
                      SHIPMENT PROGRESS
                    </p>
                    <div className="flex items-center gap-0">
                      {statusSteps.map((step, stepIdx) => {
                        const isCompleted = stepIdx <= currentStepIdx;
                        const isCurrent = stepIdx === currentStepIdx;
                        const stepLabels = {
                          pending_confirmation: 'PENDING',
                          confirmed: 'CONFIRMED',
                          in_transit: 'IN TRANSIT',
                          delivered: 'DELIVERED',
                        };
                        return (
                          <React.Fragment key={`step-${order.id}-${step}`}>
                            <div className="flex flex-col items-center gap-1 flex-1">
                              <div
                                className="w-6 h-6 flex items-center justify-center border-2 transition-all"
                                style={{
                                  borderColor: isCompleted ? 'var(--secondary)' : 'var(--border)',
                                  backgroundColor: isCompleted
                                    ? isCurrent
                                      ? 'var(--secondary)'
                                      : 'var(--success-bg)' : 'var(--background)',
                                }}
                              >
                                {isCompleted && (
                                  <div
                                    className="w-2 h-2"
                                    style={{ backgroundColor: isCurrent ? 'white' : 'var(--secondary)' }}
                                  />
                                )}
                              </div>
                              <span
                                className="text-xs font-heading tracking-stamp text-center leading-tight hidden sm:block"
                                style={{
                                  fontFamily: 'var(--font-heading)',
                                  fontSize: '9px',
                                  color: isCompleted ? 'var(--secondary)' : 'var(--muted-foreground)',
                                }}
                              >
                                {stepLabels[step]}
                              </span>
                            </div>
                            {stepIdx < statusSteps.length - 1 && (
                              <div
                                className="flex-1 h-0.5 mb-4"
                                style={{
                                  backgroundColor: stepIdx < currentStepIdx ? 'var(--secondary)' : 'var(--border)',
                                }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  {order.status === 'delivered' && (
                    <div className="px-4 pb-3">
                      <a
                        href={`/marketplace/${order.listingId}`}
                        className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
                      >
                        <Package size={12} /> REORDER THIS ITEM
                      </a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Negotiations — bulk-buyer price offers, capped at 10% ─────────────────
function hoursUntil(dateStr) {
  const ms = new Date(dateStr).getTime() - Date.now();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60)));
}

function NegotiationStatusBadge({ status }) {
  const map = {
    pending: { label: 'AWAITING YOUR RESPONSE', color: 'var(--amber)', bg: 'var(--amber-bg)' },
    countered: { label: 'COUNTER SENT', color: 'var(--amber)', bg: 'var(--amber-bg)' },
    accepted: { label: 'ACCEPTED', color: 'var(--secondary)', bg: 'var(--success-bg)' },
    rejected: { label: 'REJECTED', color: 'var(--danger)', bg: 'var(--danger-bg)' },
    expired: { label: 'EXPIRED', color: 'var(--muted-foreground)', bg: 'var(--background)' },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className="text-xs font-heading tracking-stamp px-2 py-0.5 border inline-block"
      style={{ fontFamily: 'var(--font-heading)', borderColor: s.color, color: s.color, backgroundColor: s.bg }}
    >
      {s.label}
    </span>
  );
}

function SellerNegotiationsTab({ negotiations, loading, onRespond }) {
  const [counterInputs, setCounterInputs] = useState({});
  const [busyId, setBusyId] = useState('');

  const act = async (id, action, counterPrice) => {
    setBusyId(id);
    try {
      await onRespond(id, action, counterPrice);
    } finally {
      setBusyId('');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)' }} /></div>;
  }
  if (negotiations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Handshake size={28} className="mb-3" style={{ color: 'var(--muted-foreground)' }} />
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No bulk price offers yet.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {negotiations.map((n) => (
        <div key={n.id} className="border p-3" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="text-sm font-medium">{n.listingTitle}</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                {n.buyerCompany || n.buyerName} · {n.quantity.toLocaleString('en-IN')} units
              </p>
            </div>
            <NegotiationStatusBadge status={n.status} />
          </div>

          <div className="grid grid-cols-3 gap-2 mb-2 text-xs">
            <div><span style={{ color: 'var(--muted-foreground)' }}>Listed</span><p className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{formatINR(n.listingPrice)}</p></div>
            <div><span style={{ color: 'var(--muted-foreground)' }}>Offered</span><p className="font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: 'var(--secondary)' }}>{formatINR(n.offeredPrice)} ({n.discountPercent}% off)</p></div>
            {n.counterPrice != null && (
              <div><span style={{ color: 'var(--muted-foreground)' }}>Your counter</span><p className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{formatINR(n.counterPrice)} ({n.counterDiscountPercent}% off)</p></div>
            )}
          </div>

          {n.status === 'pending' && (
            <>
              <p className="text-xs mb-2 flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                <Clock size={11} /> Expires in {hoursUntil(n.expiresAt)}h if you don't respond.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => act(n.id, 'accept')} disabled={busyId === n.id} className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1 disabled:opacity-60">
                  <ThumbsUp size={12} /> ACCEPT
                </button>
                <button onClick={() => act(n.id, 'reject')} disabled={busyId === n.id} className="btn-danger px-3 py-1.5 text-xs flex items-center gap-1 disabled:opacity-60">
                  <ThumbsDown size={12} /> REJECT
                </button>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={0.01}
                    step="0.01"
                    placeholder="Counter ₹/unit"
                    value={counterInputs[n.id] || ''}
                    onChange={(e) => setCounterInputs((p) => ({ ...p, [n.id]: e.target.value }))}
                    className="input-ledger px-2 py-1.5 text-xs w-28 font-mono-data"
                    style={{ fontFamily: 'var(--font-mono)' }}
                  />
                  <button
                    onClick={() => act(n.id, 'counter', Number(counterInputs[n.id]))}
                    disabled={busyId === n.id || !counterInputs[n.id]}
                    className="btn-secondary px-3 py-1.5 text-xs disabled:opacity-60"
                  >
                    COUNTER
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function BuyerNegotiationsTab({ negotiations, loading, onRespondToCounter, onPayNow }) {
  const [busyId, setBusyId] = useState('');

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await onRespondToCounter(id, action);
    } finally {
      setBusyId('');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)' }} /></div>;
  }
  if (negotiations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Handshake size={28} className="mb-3" style={{ color: 'var(--muted-foreground)' }} />
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          No bulk price offers yet — enter a bulk quantity on an eligible listing to negotiate.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {negotiations.map((n) => (
        <div key={n.id} className="border p-3" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="text-sm font-medium">{n.listingTitle}</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                {n.sellerCompany} · {n.quantity.toLocaleString('en-IN')} units
              </p>
            </div>
            <NegotiationStatusBadge status={n.status} />
          </div>

          <div className="grid grid-cols-3 gap-2 mb-2 text-xs">
            <div><span style={{ color: 'var(--muted-foreground)' }}>Listed</span><p className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{formatINR(n.listingPrice)}</p></div>
            <div><span style={{ color: 'var(--muted-foreground)' }}>Your offer</span><p className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{formatINR(n.offeredPrice)} ({n.discountPercent}% off)</p></div>
            {n.counterPrice != null && (
              <div><span style={{ color: 'var(--muted-foreground)' }}>Seller's counter</span><p className="font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: 'var(--secondary)' }}>{formatINR(n.counterPrice)} ({n.counterDiscountPercent}% off)</p></div>
            )}
          </div>

          {n.status === 'pending' && (
            <p className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
              <Clock size={11} /> Waiting for the seller — expires in {hoursUntil(n.expiresAt)}h.
            </p>
          )}

          {n.status === 'countered' && (
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => act(n.id, 'accept')} disabled={busyId === n.id} className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1 disabled:opacity-60">
                <ThumbsUp size={12} /> ACCEPT COUNTER
              </button>
              <button onClick={() => act(n.id, 'reject')} disabled={busyId === n.id} className="btn-danger px-3 py-1.5 text-xs flex items-center gap-1 disabled:opacity-60">
                <ThumbsDown size={12} /> DECLINE
              </button>
            </div>
          )}

          {n.status === 'accepted' && !n.resolvedOrderId && (
            <button onClick={() => onPayNow(n)} className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5">
              <Handshake size={12} /> PAY NOW AT {formatINR(n.offeredPrice)}/UNIT
            </button>
          )}
          {n.status === 'accepted' && n.resolvedOrderId && (
            <p className="text-xs" style={{ color: 'var(--secondary)' }}>✓ Order placed at this negotiated price.</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Buyer's saved shipping addresses (up to 3) ────────────────────────────
function AddressFormFields({ value, onChange }) {
  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value });
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="Label (e.g. Home)" value={value.label || ''} onChange={set('label')} className="input-ledger px-3 py-2 text-sm" />
        <input placeholder="Full Name *" value={value.fullName || ''} onChange={set('fullName')} className="input-ledger px-3 py-2 text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="Mobile Number *" value={value.mobileNumber || ''} onChange={set('mobileNumber')} className="input-ledger px-3 py-2 text-sm font-mono-data" style={{ fontFamily: 'var(--font-mono)' }} />
        <input placeholder="Alternative Mobile (optional)" value={value.alternativeMobileNumber || ''} onChange={set('alternativeMobileNumber')} className="input-ledger px-3 py-2 text-sm font-mono-data" style={{ fontFamily: 'var(--font-mono)' }} />
      </div>
      <input placeholder="Address Line 1 *" value={value.addressLine1 || ''} onChange={set('addressLine1')} className="input-ledger px-3 py-2 text-sm" />
      <input placeholder="Address Line 2 (optional)" value={value.addressLine2 || ''} onChange={set('addressLine2')} className="input-ledger px-3 py-2 text-sm" />
      <div className="grid grid-cols-3 gap-2">
        <input placeholder="City *" value={value.city || ''} onChange={set('city')} className="input-ledger px-3 py-2 text-sm" />
        <input placeholder="State *" value={value.state || ''} onChange={set('state')} className="input-ledger px-3 py-2 text-sm" />
        <input placeholder="Pincode *" value={value.pincode || ''} onChange={set('pincode')} className="input-ledger px-3 py-2 text-sm font-mono-data" style={{ fontFamily: 'var(--font-mono)' }} />
      </div>
    </div>
  );
}

function BuyerAddressesTab({ addresses, loading, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // address being edited, or null for "add new"
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState('');

  const openNew = () => { setEditing(null); setForm({}); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm(a); setShowForm(true); };

  const handleSave = async () => {
    const { fullName, mobileNumber, addressLine1, city, state, pincode } = form;
    if (!fullName || !mobileNumber || !addressLine1 || !city || !state || !pincode) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSaving(true);
    try {
      if (editing) await addressesApi.update(editing.id, form);
      else await addressesApi.create(form);
      toast.success(editing ? 'Address updated.' : 'Address saved.');
      setShowForm(false);
      onRefresh();
    } catch (err) {
      toast.error(err.message || 'Could not save this address.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setBusyId(id);
    try {
      await addressesApi.remove(id);
      toast.success('Address removed.');
      onRefresh();
    } catch (err) {
      toast.error(err.message || 'Could not remove this address.');
    } finally {
      setBusyId('');
    }
  };

  const handleSetDefault = async (id) => {
    setBusyId(id);
    try {
      await addressesApi.setDefault(id);
      toast.success('Default address updated.');
      onRefresh();
    } catch (err) {
      toast.error(err.message || 'Could not update default address.');
    } finally {
      setBusyId('');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)' }} /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          {addresses.length}/3 saved addresses — used to speed up checkout.
        </p>
        {addresses.length < 3 && !showForm && (
          <button onClick={openNew} className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5">
            <Plus size={12} /> ADD ADDRESS
          </button>
        )}
      </div>

      {showForm && (
        <div className="border p-3 mb-4" style={{ borderColor: 'var(--primary)', backgroundColor: 'rgba(193,64,29,0.04)' }}>
          <p className="text-xs font-heading tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--primary)' }}>
            {editing ? 'EDIT ADDRESS' : 'NEW ADDRESS'}
          </p>
          <AddressFormFields value={form} onChange={setForm} />
          <div className="flex gap-2 mt-3">
            <button onClick={() => setShowForm(false)} className="btn-secondary px-3 py-1.5 text-xs">CANCEL</button>
            <button onClick={handleSave} disabled={saving} className="btn-primary px-3 py-1.5 text-xs disabled:opacity-60">
              {saving ? 'SAVING...' : 'SAVE ADDRESS'}
            </button>
          </div>
        </div>
      )}

      {addresses.length === 0 && !showForm ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Package size={28} className="mb-3" style={{ color: 'var(--muted-foreground)' }} />
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            No saved addresses yet — add one now, or save one during your next checkout.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {addresses.map((a) => (
            <div key={a.id} className="border p-3 flex items-start justify-between gap-3" style={{ borderColor: 'var(--border)' }}>
              <div className="text-sm">
                <p className="font-medium">
                  {a.label ? `${a.label} · ` : ''}{a.fullName} {a.isDefault && <span className="text-xs" style={{ color: 'var(--secondary)' }}>· DEFAULT</span>}
                </p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {a.addressLine1}{a.addressLine2 ? `, ${a.addressLine2}` : ''}, {a.city}, {a.state} {a.pincode}
                </p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {a.mobileNumber}{a.alternativeMobileNumber ? ` / ${a.alternativeMobileNumber}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {!a.isDefault && (
                  <button onClick={() => handleSetDefault(a.id)} disabled={busyId === a.id} className="btn-secondary px-2 py-1 text-xs disabled:opacity-60">
                    SET DEFAULT
                  </button>
                )}
                <button onClick={() => openEdit(a)} className="p-1.5 border" style={{ borderColor: 'var(--border)' }} title="Edit">
                  <Edit2 size={12} />
                </button>
                <button onClick={() => handleDelete(a.id)} disabled={busyId === a.id} className="p-1.5 border disabled:opacity-60" style={{ borderColor: 'var(--danger)' }} title="Delete">
                  <Trash2 size={12} style={{ color: 'var(--danger)' }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Platform Stats Panel ─────────────────────────────────
// Personal account summary — scoped entirely to the logged-in seller's or
// buyer's own activity. (Order pipeline and quick actions have their own
// dedicated sidebar cards further down, already personal-scoped — this
// panel is just the top-line numbers.)
function MyAccountPanel({ isSeller, orders, listingCount }) {
  const totalSpendOrEarned = orders
    .filter((o) => o.status === 'delivered')
    .reduce((acc, o) => acc + (isSeller ? o.totalValue : o.totalPayable || o.totalValue), 0);

  return (
    <div
      className="border-2 border-foreground p-4"
      style={{ backgroundColor: 'var(--foreground)', boxShadow: '3px 3px 0 var(--primary)' }}
    >
      <div className="flex items-center gap-2 mb-4">
        <BarChart2 size={14} style={{ color: 'var(--primary)' }} />
        <h3
          className="font-heading text-sm tracking-stamp"
          style={{ color: 'var(--background)', fontFamily: 'var(--font-heading)' }}
        >
          MY ACCOUNT SUMMARY
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xl font-heading font-mono-data" style={{ color: 'var(--background)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
            {orders.length}
          </p>
          <p className="text-xs font-heading tracking-stamp" style={{ color: 'rgba(237,232,220,0.6)', fontFamily: 'var(--font-heading)' }}>
            {isSeller ? 'ORDERS RECEIVED' : 'ORDERS PLACED'}
          </p>
        </div>
        <div>
          <p className="text-xl font-heading font-mono-data" style={{ color: 'var(--background)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
            {isSeller ? listingCount : orders.filter((o) => o.status === 'delivered').length}
          </p>
          <p className="text-xs font-heading tracking-stamp" style={{ color: 'rgba(237,232,220,0.6)', fontFamily: 'var(--font-heading)' }}>
            {isSeller ? 'MY LISTINGS' : 'DELIVERED'}
          </p>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(237,232,220,0.2)' }}>
        <p className="text-xs font-heading tracking-stamp" style={{ color: 'rgba(237,232,220,0.6)', fontFamily: 'var(--font-heading)' }}>
          {isSeller ? 'TOTAL SOLD (DELIVERED)' : 'TOTAL SPENT (DELIVERED)'}
        </p>
        <p className="text-sm font-mono-data font-medium" style={{ color: 'var(--secondary)', fontFamily: 'var(--font-mono)' }}>
          {formatINR(totalSpendOrEarned)}
        </p>
      </div>
    </div>
  );
}

// ─── Main Dashboard Client ────────────────────────────────
export default function DashboardClient() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Seller state
  const [sellerListings, setSellerListings] = useState([]);
  const [sellerOrders, setSellerOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('listings');
  const [showListingModal, setShowListingModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Buyer state
  const [buyerOrders, setBuyerOrders] = useState([]);

  // Negotiations (bulk-buyer price offers)
  const [negotiations, setNegotiations] = useState([]);
  const [negotiationsLoading, setNegotiationsLoading] = useState(false);
  const [orderTarget, setOrderTarget] = useState(null); // reused OrderModal, opened for an accepted negotiation

  // Saved shipping addresses (buyer only, up to 3)
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);

  const loadAddresses = useCallback(async () => {
    setAddressesLoading(true);
    try {
      const res = await addressesApi.list();
      setSavedAddresses(res?.addresses ?? []);
    } catch (err) {
      toast.error(err.message || 'Could not load your addresses.');
    } finally {
      setAddressesLoading(false);
    }
  }, []);

  const loadNegotiations = useCallback(async (role) => {
    setNegotiationsLoading(true);
    try {
      const res = role === 'seller' ? await negotiationsApi.received() : await negotiationsApi.mine();
      setNegotiations(res?.negotiations ?? []);
    } catch (err) {
      toast.error(err.message || 'Could not load negotiations.');
    } finally {
      setNegotiationsLoading(false);
    }
  }, []);

  // Auth check
  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    const storedUser = getStoredUser();
    setUser(storedUser);
    setAuthChecked(true);
  }, [navigate]);

  // Load role-specific data once we know who's logged in.
  useEffect(() => {
    if (!authChecked || !user) return;

    let cancelled = false;
    setDataLoading(true);
    setLoadError('');

    const loadData = async () => {
      try {
        if (user.role === 'seller') {
          const [listingsRes, ordersRes] = await Promise.all([
            listingsApi.mine(),
            ordersApi.list(),
          ]);
          if (cancelled) return;
          setSellerListings(listingsRes?.listings ?? []);
          setSellerOrders(ordersRes?.orders ?? []);
        } else {
          const ordersRes = await ordersApi.list();
          if (cancelled) return;
          setBuyerOrders(ordersRes?.orders ?? []);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    };

    loadData();
    loadNegotiations(user.role);
    if (user.role === 'buyer') loadAddresses();
    return () => {
      cancelled = true;
    };
  }, [authChecked, user, loadNegotiations, loadAddresses]);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  // Seller: save listing
  const handleSaveListing = async (data) => {
    try {
      if (editTarget) {
        const { listing, resubmitted } = await listingsApi.update(editTarget.id, data);
        setSellerListings((prev) => prev.map((l) => (l.id === editTarget.id ? listing : l)));
        toast.success(
          resubmitted
            ? 'Changes saved — resubmitted for admin review.'
            : 'Listing updated successfully.'
        );
      } else {
        const { listing } = await listingsApi.create(data);
        setSellerListings((prev) => [listing, ...prev]);
        toast.success('Listing submitted! It will appear in the marketplace once an admin approves it.');
      }
      setShowListingModal(false);
      setEditTarget(null);
    } catch (err) {
      toast.error(err.message || 'Could not save this listing.');
    }
  };

  // Seller: quick pause/reactivate without opening the full edit form.
  const handleToggleListingStatus = async (listing) => {
    const nextStatus = listing.status === 'active' ? 'inactive' : 'active';
    try {
      const { listing: updated } = await listingsApi.update(listing.id, { status: nextStatus });
      setSellerListings((prev) => prev.map((l) => (l.id === listing.id ? updated : l)));
      toast.success(nextStatus === 'active' ? 'Listing reactivated.' : 'Listing paused — hidden from the marketplace.');
    } catch (err) {
      toast.error(err.message || 'Could not update this listing.');
    }
  };

  // Seller: delete listing
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await listingsApi.remove(deleteTarget.id);
      setSellerListings((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      toast.success(`"${deleteTarget.title.slice(0, 40)}..." deleted.`);
    } catch (err) {
      toast.error(err.message || 'Could not delete this listing.');
    } finally {
      setDeleteTarget(null);
    }
  };

  // Seller: advance order status
  const handleAdvanceOrder = async (orderId, newStatus, extra) => {
    try {
      const { order } = await ordersApi.advanceStatus(orderId, newStatus, extra);
      setSellerOrders((prev) => prev.map((o) => (o.id === orderId ? order : o)));
      const statusLabels = {
        confirmed: 'Order confirmed.',
        in_transit: 'Order marked as shipped — tracking details sent to the buyer.',
        delivered: 'Order marked as delivered. This deal counts toward your GMV.',
      };
      toast.success(statusLabels[newStatus] ?? 'Order status updated.');
    } catch (err) {
      toast.error(err.message || 'Could not update this order.');
    }
  };

  // Seller: accept / reject / counter a received bulk offer.
  const handleRespondToNegotiation = async (id, action, counterPrice) => {
    try {
      await negotiationsApi.respond(id, action, counterPrice);
      const labels = { accept: 'Offer accepted.', reject: 'Offer rejected.', counter: 'Counter-offer sent.' };
      toast.success(labels[action] || 'Updated.');
      loadNegotiations('seller');
    } catch (err) {
      toast.error(err.message || 'Could not update this offer.');
    }
  };

  // Buyer: accept / reject a seller's counter-offer.
  const handleRespondToCounter = async (id, action) => {
    try {
      await negotiationsApi.respondToCounter(id, action);
      toast.success(action === 'accept' ? 'Counter-offer accepted — you can now pay.' : 'Counter-offer declined.');
      loadNegotiations('buyer');
    } catch (err) {
      toast.error(err.message || 'Could not respond to this counter-offer.');
    }
  };

  // Buyer: open checkout for an accepted negotiation. OrderModal only needs
  // a handful of fields for this flow (quantity/price are already fixed by
  // the negotiation) — build a minimal listing-shaped object from it.
  const handlePayNowForNegotiation = (negotiation) => {
    setOrderTarget({
      id: negotiation.listingId,
      title: negotiation.listingTitle,
      price: negotiation.listingPrice,
      quantity: negotiation.quantity,
      moq: negotiation.quantity,
      sellerCompany: negotiation.sellerCompany,
      negotiable: false,
      __negotiation: negotiation,
    });
  };

  const handleNegotiatedOrderSuccess = () => {
    toast.success('Payment confirmed! Your order has been sent to the seller.');
    setOrderTarget(null);
    loadNegotiations('buyer');
    ordersApi.list().then((res) => setBuyerOrders(res?.orders ?? []));
  };

  if (!authChecked || dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center">
          <div
            className="inline-block w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mb-3"
            style={{ borderColor: 'var(--primary)' }}
          />
          <p className="text-sm font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
            LOADING DASHBOARD...
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center max-w-sm">
          <AlertCircle size={28} className="mx-auto mb-3" style={{ color: 'var(--danger)' }} />
          <p className="text-sm font-heading tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            COULDN'T LOAD DASHBOARD
          </p>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{loadError}</p>
        </div>
      </div>
    );
  }

  const isSeller = user?.role === 'seller';

  // KPI data
  const pendingOrdersCount = isSeller
    ? sellerOrders.filter((o) => o.status === 'pending_confirmation').length
    : buyerOrders.filter((o) => o.status === 'pending_confirmation').length;

  const activeListingsCount = isSeller
    ? sellerListings.filter((l) => l.status === 'active').length
    : buyerOrders.length; // buyers: total orders they've personally placed

  const gmvValue = isSeller
    ? sellerOrders
        .filter((o) => o.status === 'delivered')
        .reduce((acc, o) => acc + o.totalValue, 0)
    : buyerOrders.reduce((acc, o) => acc + (o.totalPayable || o.totalValue), 0);

  const dealsClosed = isSeller
    ? sellerOrders.filter((o) => o.status === 'delivered').length
    : buyerOrders.filter((o) => o.status === 'delivered').length;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background)' }}>
      <AppHeader
        userName={user?.name}
        userRole={user?.role}
        onLogout={handleLogout}
        currentPath="/dashboard"
      />

      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 lg:px-8 xl:px-10 2xl:px-16 py-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1 h-6" style={{ backgroundColor: 'var(--primary)' }} />
              <h1
                className="font-heading text-2xl tracking-stamp"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {isSeller ? 'SELLER DASHBOARD' : 'BUYER DASHBOARD'}
              </h1>
            </div>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Welcome back,{' '}
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>
                {user?.name}
              </span>
              {user?.company && (
                <>
                  {' '}·{' '}
                  <span style={{ color: 'var(--muted-foreground)' }}>{user.company}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted-foreground)' }}>
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: 'var(--secondary)' }} />
            Last refreshed: {new Date().toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        {/* KPI Bento Grid — 4 cards: grid-cols-4, single row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            label={isSeller ? 'MY GMV (DELIVERED)' : 'TOTAL SPEND'}
            value={formatINR(gmvValue)}
            subValue={isSeller ? 'From delivered orders' : 'Across all orders'}
            icon={TrendingUp}
            variant="default"
          />
          <KpiCard
            label="DEALS CLOSED"
            value={dealsClosed.toString()}
            subValue="Orders at delivered status"
            icon={CheckCircle}
            variant="success"
          />
          <KpiCard
            label={isSeller ? 'ACTIVE LISTINGS' : 'ORDERS PLACED'}
            value={activeListingsCount.toString()}
            subValue={isSeller ? 'Live on marketplace' : 'Total orders you\'ve placed'}
            icon={Package}
            variant="default"
          />
          <KpiCard
            label="PENDING CONFIRMATION"
            value={pendingOrdersCount.toString()}
            subValue={
              pendingOrdersCount > 0
                ? isSeller
                  ? 'Awaiting your confirmation' : 'Awaiting seller confirmation' : 'All orders up to date'
            }
            icon={AlertCircle}
            variant={pendingOrdersCount > 0 ? 'alert' : 'default'}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          {/* Left — Main Data Panel (spans 2/3 or 3/4) */}
          <div className="xl:col-span-2 2xl:col-span-3">
            {/* Tab Navigation */}
            {isSeller ? (
              <>
                <div className="flex gap-2 mb-4">
                  {[
                    { key: 'listings', label: 'MY LISTINGS', count: sellerListings.length },
                    {
                      key: 'orders',
                      label: 'ORDERS RECEIVED',
                      count: sellerOrders.length,
                      alert: pendingOrdersCount,
                    },
                    {
                      key: 'negotiations',
                      label: 'NEGOTIATIONS',
                      count: negotiations.length,
                      alert: negotiations.filter((n) => n.status === 'pending').length,
                    },
                  ].map((tab) => (
                    <button
                      key={`tab-${tab.key}`}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-all duration-150 ${
                        activeTab === tab.key ? 'tab-active' : 'tab-inactive'
                      }`}
                    >
                      {tab.label}
                      <span
                        className="font-mono-data text-xs px-1.5 py-0.5"
                        style={{
                          fontFamily: 'var(--font-mono)',
                          backgroundColor:
                            activeTab === tab.key
                              ? 'rgba(255,255,255,0.2)'
                              : 'var(--muted)',
                          color: activeTab === tab.key ? 'white' : 'var(--muted-foreground)',
                        }}
                      >
                        {tab.count}
                      </span>
                      {tab.alert && tab.alert > 0 ? (
                        <span
                          className="font-mono-data text-xs px-1.5 py-0.5"
                          style={{
                            backgroundColor: 'var(--danger)',
                            color: 'white',
                            fontFamily: 'var(--font-mono)',
                          }}
                        >
                          {tab.alert} !
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div
                  className="border-2 border-foreground p-4"
                  style={{ backgroundColor: 'var(--card)', boxShadow: '2px 2px 0 var(--muted)' }}
                >
                  {activeTab === 'listings' ? (
                    <SellerListingsTab
                      listings={sellerListings}
                      onEdit={(l) => { setEditTarget(l); setShowListingModal(true); }}
                      onDelete={setDeleteTarget}
                      onNew={() => { setEditTarget(null); setShowListingModal(true); }}
                      onToggleStatus={handleToggleListingStatus}
                    />
                  ) : activeTab === 'negotiations' ? (
                    <SellerNegotiationsTab
                      negotiations={negotiations}
                      loading={negotiationsLoading}
                      onRespond={handleRespondToNegotiation}
                    />
                  ) : (
                    <SellerOrdersTab
                      orders={sellerOrders}
                      onAdvance={handleAdvanceOrder}
                    />
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2 mb-4">
                  {[
                    { key: 'orders', label: 'MY ORDERS', count: buyerOrders.length },
                    {
                      key: 'negotiations',
                      label: 'NEGOTIATIONS',
                      count: negotiations.length,
                      alert: negotiations.filter((n) => n.status === 'countered' || (n.status === 'accepted' && !n.resolvedOrderId)).length,
                    },
                    { key: 'addresses', label: 'MY ADDRESSES', count: savedAddresses.length },
                  ].map((tab) => (
                    <button
                      key={`buyer-tab-${tab.key}`}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-all duration-150 ${
                        activeTab === tab.key || (tab.key === 'orders' && activeTab === 'listings') ? 'tab-active' : 'tab-inactive'
                      }`}
                    >
                      {tab.label}
                      <span
                        className="font-mono-data text-xs px-1.5 py-0.5"
                        style={{
                          fontFamily: 'var(--font-mono)',
                          backgroundColor:
                            activeTab === tab.key || (tab.key === 'orders' && activeTab === 'listings')
                              ? 'rgba(255,255,255,0.2)'
                              : 'var(--muted)',
                          color: activeTab === tab.key || (tab.key === 'orders' && activeTab === 'listings') ? 'white' : 'var(--muted-foreground)',
                        }}
                      >
                        {tab.count}
                      </span>
                      {tab.alert && tab.alert > 0 ? (
                        <span
                          className="font-mono-data text-xs px-1.5 py-0.5"
                          style={{ backgroundColor: 'var(--danger)', color: 'white', fontFamily: 'var(--font-mono)' }}
                        >
                          {tab.alert} !
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
                <div
                  className="border-2 border-foreground p-4"
                  style={{ backgroundColor: 'var(--card)', boxShadow: '2px 2px 0 var(--muted)' }}
                >
                  {activeTab === 'negotiations' ? (
                    <BuyerNegotiationsTab
                      negotiations={negotiations}
                      loading={negotiationsLoading}
                      onRespondToCounter={handleRespondToCounter}
                      onPayNow={handlePayNowForNegotiation}
                    />
                  ) : activeTab === 'addresses' ? (
                    <BuyerAddressesTab
                      addresses={savedAddresses}
                      loading={addressesLoading}
                      onRefresh={loadAddresses}
                    />
                  ) : (
                    <BuyerOrdersTab orders={buyerOrders} />
                  )}
                </div>
              </>
            )}

            {/* GMV Chart */}
            <div
              className="mt-6 border-2 border-foreground p-4"
              style={{ backgroundColor: 'var(--card)', boxShadow: '2px 2px 0 var(--muted)' }}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3
                    className="font-heading text-sm tracking-stamp"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    MY {isSeller ? 'SALES' : 'SPEND'} TREND
                  </h3>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                    Your own orders, 8-week view · ₹ — Dashed line: order count
                  </p>
                </div>
              </div>
              <Suspense fallback={<ChartFallback height={220} />}>
                <GmvChart orders={isSeller ? sellerOrders : buyerOrders} valueField={isSeller ? 'totalValue' : 'totalPayable'} />
              </Suspense>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="xl:col-span-1 flex flex-col gap-4">
            {/* My Account Summary */}
            <MyAccountPanel isSeller={isSeller} orders={isSeller ? sellerOrders : buyerOrders} listingCount={sellerListings.length} />

            {/* Category Breakdown */}
            <div
              className="border-2 border-foreground p-4"
              style={{ backgroundColor: 'var(--card)', boxShadow: '2px 2px 0 var(--muted)' }}
            >
              <h3
                className="font-heading text-sm tracking-stamp mb-1"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                MY {isSeller ? 'SALES' : 'SPEND'} BY CATEGORY
              </h3>
              <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                Your own transaction volume breakdown
              </p>
              <Suspense fallback={<ChartFallback height={200} />}>
                <CategoryChart orders={isSeller ? sellerOrders : buyerOrders} valueField={isSeller ? 'totalValue' : 'totalPayable'} />
              </Suspense>
            </div>

            {/* Order Pipeline Summary */}
            <div
              className="border-2 border-foreground p-4"
              style={{ backgroundColor: 'var(--card)', boxShadow: '2px 2px 0 var(--muted)' }}
            >
              <h3
                className="font-heading text-sm tracking-stamp mb-3"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                ORDER PIPELINE
              </h3>
              {[
                { status: 'pending_confirmation', label: 'PENDING', count: isSeller ? sellerOrders.filter(o => o.status === 'pending_confirmation').length : buyerOrders.filter(o => o.status === 'pending_confirmation').length },
                { status: 'confirmed', label: 'CONFIRMED', count: isSeller ? sellerOrders.filter(o => o.status === 'confirmed').length : buyerOrders.filter(o => o.status === 'confirmed').length },
                { status: 'in_transit', label: 'IN TRANSIT', count: isSeller ? sellerOrders.filter(o => o.status === 'in_transit').length : buyerOrders.filter(o => o.status === 'in_transit').length },
                { status: 'delivered', label: 'DELIVERED', count: isSeller ? sellerOrders.filter(o => o.status === 'delivered').length : buyerOrders.filter(o => o.status === 'delivered').length },
              ].map((row) => (
                <div
                  key={`pipeline-${row.status}`}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <StatusBadge status={row.status} size="sm" />
                  <span
                    className="font-mono-data text-sm font-medium"
                    style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {row.count}
                  </span>
                </div>
              ))}
            </div>

            {/* Quick Links */}
            <div
              className="border border-border p-4"
              style={{ backgroundColor: 'var(--background)' }}
            >
              <h3
                className="font-heading text-xs tracking-stamp mb-3"
                style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}
              >
                QUICK ACTIONS
              </h3>
              <div className="flex flex-col gap-2">
                {isSeller ? (
                  <>
                    <button
                      onClick={() => { setEditTarget(null); setShowListingModal(true); }}
                      className="btn-primary px-3 py-2 text-xs flex items-center gap-2"
                    >
                      <Plus size={13} />
                      NEW LISTING
                    </button>
                    <a
                      href="/marketplace"
                      className="btn-secondary px-3 py-2 text-xs flex items-center gap-2"
                    >
                      <Package size={13} />
                      VIEW MARKETPLACE
                    </a>
                  </>
                ) : (
                  <>
                    <a
                      href="/marketplace"
                      className="btn-primary px-3 py-2 text-xs flex items-center gap-2"
                    >
                      <Package size={13} />
                      BROWSE LISTINGS
                    </a>
                    <a
                      href="/marketplace"
                      className="btn-secondary px-3 py-2 text-xs flex items-center gap-2"
                    >
                      <TrendingUp size={13} />
                      FIND DEALS
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <AppFooter />

      {/* Create/Edit Listing Modal */}
      {showListingModal && (
        <ListingFormModal
          editListing={editTarget}
          onClose={() => { setShowListingModal(false); setEditTarget(null); }}
          onSave={handleSaveListing}
        />
      )}

      {/* Delete Confirm Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Listing"
        message={`Delete "${deleteTarget?.title}"? This will remove the listing from the marketplace and cannot be undone.`}
        confirmLabel="DELETE LISTING"
        cancelLabel="KEEP LISTING"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />

      {/* Pay Now — checkout for an accepted bulk negotiation */}
      {orderTarget && (
        <OrderModal
          listing={orderTarget}
          initialNegotiation={orderTarget.__negotiation}
          onClose={() => setOrderTarget(null)}
          onSuccess={handleNegotiatedOrderSuccess}
        />
      )}

      <Toaster position="bottom-right" richColors />
    </div>
  );
}
